/**
 * Los emojis del texto de la plantilla (pedido de Alan del 26/10/2026: «que me deje tocar algo de emojis que me traiga
 * todos los emojis que existen así puedo elegir»). Los datos son los de Unicode en español (`emojibase-data/es`, de
 * CLDR): cada emoji trae su nombre y sus palabras clave, así se busca «corazón» o «fuego» y no «heart».
 *
 * Lógica pura; el selector (`components/editor-plantilla/SelectorEmojis.tsx`) baja los datos recién al abrirse.
 */

/** El tope del validador del backend (`LIMITES.emojis` de plantilla.util): con más, la plantilla no se manda a aprobar. */
export const MAX_EMOJIS = 10;

/** Lo que se usa de `emojibase-data/es/compact.json`. */
export interface EmojiCompacto {
  label: string;
  hexcode: string;
  unicode: string;
  group?: number;
  order?: number;
  tags?: string[];
}

/** Lo que se usa de `emojibase-data/es/messages.json`. */
export interface MensajesEmojis {
  groups: { key: string; message: string; order: number }[];
}

export interface GrupoEmojis {
  numero: number;
  nombre: string;
  emojis: EmojiCompacto[];
}

/** El grupo «componentes» de Unicode: tonos de piel y pelos sueltos, que solos no son un emoji para mandar. */
const GRUPO_COMPONENTES = 2;

/** `Intl.Segmenter` (ES2022): el front compila con la lib ES2020, que no lo declara. Los navegadores lo tienen. */
type Segmentador = { segment(t: string): Iterable<{ segment: string }> };
type ConSegmenter = { Segmenter: new (idioma: string, o: { granularity: "grapheme" }) => Segmentador };

let segmentador: Segmentador | null = null;
/**
 * Emojis como los cuenta una persona y como los cuenta el validador del backend (`contarEmojis` de plantilla.util): un
 * emoji compuesto (familia con ZWJ, tono de piel, bandera) es UNO.
 */
export function contarEmojis(t: string): number {
  if (!segmentador) segmentador = new (Intl as unknown as ConSegmenter).Segmenter("es", { granularity: "grapheme" });
  let n = 0;
  for (const { segment } of segmentador.segment(String(t ?? ""))) {
    if (/\p{Extended_Pictographic}|\p{Regional_Indicator}|\u20E3/u.test(segment)) n++;
  }
  return n;
}

/** Sin tildes ni mayúsculas: «Corazón» y «corazon» buscan lo mismo. */
export function normalizar(s: string): string {
  return String(s ?? "").normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();
}

/** Los grupos en el orden de Unicode, cada uno con sus emojis en orden; sin los componentes sueltos. */
export function agrupar(emojis: EmojiCompacto[], mensajes: MensajesEmojis): GrupoEmojis[] {
  const nombres = new Map(mensajes.groups.map((g) => [g.order, g.message]));
  const porGrupo = new Map<number, EmojiCompacto[]>();
  for (const e of emojis) {
    if (e.group === undefined || e.group === GRUPO_COMPONENTES) continue;
    porGrupo.set(e.group, [...(porGrupo.get(e.group) || []), e]);
  }
  return [...porGrupo.entries()]
    .sort(([a], [b]) => a - b)
    .map(([numero, lista]) => ({
      numero,
      nombre: nombres.get(numero) || "",
      emojis: [...lista].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }));
}

/**
 * Los emojis en los que cada palabra buscada es el comienzo de una palabra del nombre o de las palabras clave: «cor»
 * encuentra «corazón», y «risa» no encuentra «sonrisa».
 */
export function buscar(grupos: GrupoEmojis[], texto: string): EmojiCompacto[] {
  const palabras = normalizar(texto).split(/\s+/).filter(Boolean);
  if (!palabras.length) return [];
  return grupos.flatMap((g) => g.emojis).filter((e) => {
    const donde = [e.label, ...(e.tags || [])].flatMap((x) => normalizar(x).split(/\s+/));
    return palabras.every((p) => donde.some((d) => d.startsWith(p)));
  });
}
